'use client'
import React from 'react'
import { isMobile } from 'react-device-detect'

import type { MyTicketDetailModel } from '@/components/myTicket/model'
import ExtendEnteringTime from '@/components/purchase/components/extendParkinglot/components/enteringTime/view'
import useExtendParkinglotEnteringTimeViewModel from '@/components/purchase/components/extendParkinglot/components/enteringTime/viewModel'
import useExtendParkinglotPurchaseViewModel from '@/components/purchase/components/extendParkinglot/viewModel'
import PurchaseCompleteView from '@/components/purchase/components/shared/purchaseComplete/view'
import PurchaseConfirmInfoView from '@/components/purchase/components/shared/purchaseConfirmInfo/view'
import PurchaseHeader from '@/components/purchase/components/shared/purchaseHeader/view'
import usePurchaseHeaderViewModel from '@/components/purchase/components/shared/purchaseHeader/viewModel'
import PurchaseIframe from '@/components/purchase/components/shared/purchaseIframe/view'
import PurchaseInComplete from '@/components/purchase/components/shared/purchaseInComplete/view'
import PurchasePayment from '@/components/purchase/components/shared/purchasePayment/view'
import PurchaseTerms from '@/components/purchase/components/shared/purchaseTerms/view'

import Button from '@/shared/components/button/Button'
import Cover from '@/shared/components/Cover'
import Icons from '@/shared/components/icon/Icons'
import Confirm from '@/shared/components/modal/Confirm'
import Modal from '@/shared/components/modal/Modal'
import { Text } from '@/shared/components/typography'
import { formatTime } from '@/shared/utils/date'

const ExtendParkinglotPurchase = ({ data }: { data: MyTicketDetailModel }) => {
  const { states: enteringTimeStates, handlers: enteringTimeHandlers } = useExtendParkinglotEnteringTimeViewModel(data)

  const { states: purchaseStates, handlers: purchaseHandlers } = useExtendParkinglotPurchaseViewModel(
    data,
    enteringTimeStates.extendTime
  )

  const { states: headersStates, handlers: headersHandlers } = usePurchaseHeaderViewModel()
  const confirmTitle = (
    <div className="pb-5 pt-6 font-medium">
      <Text level="text1" weight="medium" color="text090">
        주차권 구매를 그만하시겠어요?
      </Text>
    </div>
  )

  const footer = (
    <div className="flex w-full gap-2">
      <Button text="나가기" background="bg020" color="text090" onClick={headersHandlers.handleCloseClick} />
      <Button text="계속하기" background="bg030" color="text010" onClick={headersHandlers.clickFooterContinueBtn} />
    </div>
  )

  const confirmInfoProps = {
    isOpen: enteringTimeStates.isConfirmOpen,
    onClick: enteringTimeHandlers.showPurchaseInfo,
    onClose: enteringTimeHandlers.closePurchaseInfo,
    carNumValue: data.ticket.carNum,
    usingTime: enteringTimeStates.extendTime,
    price: enteringTimeStates.price
  }

  const paymentInfo = [
    { key: '차량번호', value: data.ticket.carNum },
    { key: '연장시간', value: formatTime(enteringTimeStates.extendTime) }
  ]

  const backIcon = <Icons.BackIcon stroke="currentColor" color="#263238" />
  return (
    <Cover
      id="layer3"
      isOpen={purchaseStates.isPurchase}
      className={`left-0 flex h-[100dvh] w-screen max-w-[768px] flex-1 flex-col`}
    >
      {purchaseStates.step === 0 && (
        <>
          <PurchaseHeader
            headerRef={purchaseStates.headerRef}
            text="약관 동의"
            onClick={headersHandlers.handleCloseClick}
            backIcon={backIcon}
            onClickClose={headersHandlers.clickHeaderCloseBtn}
          />
          <PurchaseTerms contentHeight={purchaseStates.contentHeight} footerRef={purchaseStates.footerRef} />
        </>
      )}
      {purchaseStates.step === 1 && (
        <>
          <PurchaseHeader
            headerRef={purchaseStates.headerRef}
            text="주차 시간 선택"
            onClick={enteringTimeHandlers.onClickHeaderBackBtn}
            backIcon={backIcon}
            onClickClose={headersHandlers.clickHeaderCloseBtn}
          />
          <ExtendEnteringTime
            time={enteringTimeStates.time}
            selectedIndex={enteringTimeStates.selectedIndex}
            onClickTimeValue={enteringTimeHandlers.handleEnteringTimeValue}
            isSharedNoticeOpen={enteringTimeStates.isSharedNoticeOpen}
            handleSharedNoticeOpen={enteringTimeHandlers.handleSharedNoticeOpen}
            onClickTimeNextBtn={enteringTimeHandlers.onClickTimeNextBtn}
            contentHeight={purchaseStates.contentHeight}
            enteringTime={enteringTimeStates.extendTime}
            endTime={enteringTimeStates.endTime}
            footerRef={purchaseStates.footerRef}
          />
          <PurchaseConfirmInfoView props={confirmInfoProps} />
        </>
      )}

      {purchaseStates.step === 2 && (
        <PurchasePayment
          ticketName={data.ticket.ticketName}
          paymentInfo={paymentInfo}
          price={enteringTimeStates.price}
          paymentMethods={purchaseStates.paymentMethodList}
          onChangePaymentMethod={purchaseHandlers.radioHandler}
          onClickBack={purchaseHandlers.onClickHeaderBackBtn}
          onClickPayment={purchaseHandlers.onClickPayment}
          onCloseClick={headersHandlers.clickHeaderCloseBtn}
        />
      )}

      {/* 결제 성공 & 실패 */}
      {purchaseStates.isComplete && (
        <PurchaseCompleteView
          isComplete={purchaseStates.isComplete}
          data={data}
          time={enteringTimeStates.extendTime}
          carNum={data.ticket.carNum}
          onClick={purchaseHandlers.onClickShowTicketBtn}
        />
      )}
      {purchaseStates.isInComplete && (
        <PurchaseInComplete isInComplete={purchaseStates.isInComplete} onClick={purchaseHandlers.onClickRePayment} />
      )}

      {/* 결제창 모바일 & 웹 */}
      {purchaseStates.frameUrl && isMobile && <PurchaseIframe url={purchaseStates.frameUrl} />}
      {purchaseStates.frameUrl && purchaseStates.isOpen && (
        <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
          <iframe src={purchaseStates.frameUrl} className="z-100 h-full w-[768px]" scrolling="no" />
        </div>
      )}

      <Modal
        targetId="#layer3 "
        title={confirmTitle}
        isOpen={headersStates.isStopModalOpen}
        onClose={headersHandlers.clickFooterContinueBtn}
        footer={footer}
      >
        <Confirm
          bodyText="화면을 나갈 경우 작성한 정보들이 모두 사라집니다."
          className="mb-5 break-keep px-2 text-center"
        />
      </Modal>
    </Cover>
  )
}

export default ExtendParkinglotPurchase
